import { supabase } from './supabase';
import { logger } from './logger';

export type AnalysisVerdict = 'approve' | 'review' | 'reject';

export interface AnalysisReport {
  analyzed_at?: string;
  transcript?: string;
  frames?: string[];
  moderation?: { flagged: boolean; categories: string[] };
  conformity?: {
    conformity_score?: number;
    is_work_related?: boolean;
    summary?: string;
  } | null;
  technique?: {
    technique_score?: number | null;
    criteria?: { name: string; score: number | null; comment: string }[];
  } | null;
  verdict?: AnalysisVerdict;
}

export interface AdminVideo {
  id: string;
  candidateId: string;
  candidateName: string;
  videoUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  /** 'video' | 'image' — un post image se prévisualise en <img>, pas en <video> */
  mediaType: 'video' | 'image';
  textOverlay: string | null;
  status: 'uploading' | 'analyzing' | 'pending_review' | 'approved' | 'rejected';
  rejectionReason: string | null;
  reviewedAt: string | null;
  submittedAt: string;
  tags: string[] | null;
  muxPlaybackId: string | null;
  analysisStatus: 'pending' | 'running' | 'done' | 'failed' | null;
  analysisVerdict: AnalysisVerdict | null;
  analysisReport: AnalysisReport | null;
}

/**
 * URL lisible dans le navigateur : les URLs HLS Mux (.m3u8) ne sont pas
 * lues nativement par Chrome/Firefox → rendition mp4 statique.
 * Les URLs héritées (Supabase Storage) sont renvoyées telles quelles.
 */
export function webPlayableUrl(url: string): string {
  const match = url?.match(/^https:\/\/stream\.mux\.com\/([^/.]+)\.m3u8/);
  if (!match) return url;
  return `https://stream.mux.com/${match[1]}/highest.mp4`;
}

export async function fetchVideos(): Promise<AdminVideo[]> {
  const { data, error } = await supabase
    .from('candidate_videos')
    .select(`
      id,
      candidate_id,
      video_url,
      thumbnail_url,
      duration_seconds,
      media_type,
      text_overlay,
      status,
      rejection_reason,
      reviewed_at,
      created_at,
      tags,
      mux_playback_id,
      analysis_status,
      analysis_verdict,
      analysis_report,
      candidate_profiles!candidate_videos_candidate_id_fkey ( first_name, last_name )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_videos', 'videoService', { error: error.message });
    throw error;
  }
  logger.technical('fetch_videos_ok', 'videoService', { count: (data || []).length });

  return (data || []).map((v: any) => ({
    id: v.id,
    candidateId: v.candidate_id,
    candidateName: `${v.candidate_profiles?.first_name || ''} ${v.candidate_profiles?.last_name || ''}`.trim() || 'Inconnu',
    videoUrl: v.video_url,
    thumbnailUrl: v.thumbnail_url,
    durationSeconds: v.duration_seconds,
    mediaType: v.media_type === 'image' ? 'image' : 'video',
    textOverlay: v.text_overlay || null,
    status: v.status,
    rejectionReason: v.rejection_reason,
    reviewedAt: v.reviewed_at ? new Date(v.reviewed_at).toLocaleDateString('fr-FR') : null,
    submittedAt: new Date(v.created_at).toLocaleString('fr-FR'),
    tags: v.tags,
    muxPlaybackId: v.mux_playback_id || null,
    analysisStatus: v.analysis_status || null,
    analysisVerdict: v.analysis_verdict || null,
    analysisReport: v.analysis_report || null,
  }));
}

/** Relance l'analyse IA d'une vidéo (admin JWT accepté par analyze-video). */
export async function requestAnalysis(videoId: string): Promise<void> {
  logger.functional('request_analysis', 'videoService', { videoId });
  const { error } = await supabase.functions.invoke('analyze-video', {
    body: { video_id: videoId, source: 'candidate' },
  });
  if (error) {
    logger.error('request_analysis', 'videoService', { videoId, error: error.message });
    throw error;
  }
}

// ─── Réglages de modération (gating hybride) ─────────────────────────

export interface ModerationSettings {
  mode: 'human_first' | 'ai_auto';
  autoApproveThreshold: number;
  autoRejectThreshold: number;
}

export async function fetchModerationSettings(): Promise<ModerationSettings> {
  const { data, error } = await supabase
    .from('moderation_settings')
    .select('mode, auto_approve_threshold, auto_reject_threshold')
    .eq('id', true)
    .single();
  if (error) {
    logger.error('fetch_moderation_settings', 'videoService', { error: error.message });
    throw error;
  }
  return {
    mode: data.mode,
    autoApproveThreshold: data.auto_approve_threshold,
    autoRejectThreshold: data.auto_reject_threshold,
  };
}

export async function updateModerationSettings(settings: ModerationSettings): Promise<void> {
  logger.functional('update_moderation_settings', 'videoService', { ...settings });
  const { error } = await supabase
    .from('moderation_settings')
    .update({
      mode: settings.mode,
      auto_approve_threshold: settings.autoApproveThreshold,
      auto_reject_threshold: settings.autoRejectThreshold,
      updated_at: new Date().toISOString(),
    })
    .eq('id', true);
  if (error) {
    logger.error('update_moderation_settings', 'videoService', { error: error.message });
    throw error;
  }
}

export async function approveVideo(videoId: string, adminId: string): Promise<void> {
  logger.functional('approve_video', 'videoService', { videoId, adminId });
  const { error } = await supabase
    .from('candidate_videos')
    .update({
      status: 'approved',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', videoId);

  if (error) {
    logger.error('approve_video', 'videoService', { videoId, error: error.message });
    throw error;
  }
  logger.technical('approve_video_ok', 'videoService', { videoId });
}

export async function rejectVideo(videoId: string, adminId: string, reason: string): Promise<void> {
  logger.functional('reject_video', 'videoService', { videoId, adminId, reason });
  const { error } = await supabase
    .from('candidate_videos')
    .update({
      status: 'rejected',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
      rejection_reason: reason,
    })
    .eq('id', videoId);

  if (error) {
    logger.error('reject_video', 'videoService', { videoId, error: error.message });
    throw error;
  }
  logger.technical('reject_video_ok', 'videoService', { videoId });
}

export async function unpublishVideo(videoId: string, adminId: string): Promise<void> {
  logger.functional('unpublish_video', 'videoService', { videoId, adminId });
  const { error } = await supabase
    .from('candidate_videos')
    .update({
      status: 'pending_review',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
      rejection_reason: null,
    })
    .eq('id', videoId);

  if (error) {
    logger.error('unpublish_video', 'videoService', { videoId, error: error.message });
    throw error;
  }
  logger.technical('unpublish_video_ok', 'videoService', { videoId });
}
